import * as React from 'react';
import {transparentize} from 'polished';
import styled from 'styled-components';
import {ButtonVideo} from '../components';
import {CoursePromoContext} from '../context';

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0 60px;

  @media screen and (max-width: 1024px) {
    flex-wrap: wrap;
    padding: 0 16px;
    margin: 115px 21px 0;
    border-radius: 10px;
    background-color: ${({theme}) => theme.colors.primary};
  }
`;

const Avatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarBox = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 690px;
  max-width: 540px;
  border-radius: 10px;
  filter: drop-shadow(5px 5px 60px rgba(24, 24, 24, 0.8));
  overflow: hidden;

  @media screen and (max-width: 1024px) {
    width: 100%;
    max-height: 45vh;
    position: relative;
    top: -74px;
    margin-bottom: -80px;
    
    ${Avatar} {
      max-width: 100%;
      border-radius: 10px;
    }
  }
`;

const ButtonVideoWrapper = styled.div`
  position: absolute;
  right: 20px;
  bottom: 20px;
`;

const Content = styled.div`
  width: 100%;
  max-width: 580px;
  margin-top: 65px;
  margin-left: -10px;
  padding: 64px 50px 60px 70px;
  border-radius: 10px;
  background-color: ${({theme}) => theme.colors.primary};

  @media screen and (max-width: 1024px) {
    padding: 64px 16px 30px 16px;
    background-color: transparent;
    margin-top: 0;
  }
`;

const Name = styled.div`
  margin-top: 20px;
  font-size: 24px;
  font-weight: 600;
  line-height: 31px;
`;

const Message = styled.div`
  font-size: 20px;
  line-height: 30px;
  
  &::before {
    content: '';
    display: block;
    width: 60px;
    height: 60px;
    margin-bottom: 30px;
    background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0)'%3E%3Cpath d='M60 29.9995V4.28522H34.2857V29.9995H51.4285C51.4285 39.4519 43.738 47.1424 34.2857 47.1424V55.7139C48.4654 55.7139 60 44.1793 60 29.9995Z' fill='white'/%3E%3Cpath d='M0.000511169 47.1424V55.7139C14.1802 55.7139 25.7148 44.1793 25.7148 29.9995V4.28522H0.000511169V29.9995H17.1434C17.1434 39.4519 9.45289 47.1424 0.000511169 47.1424Z' fill='white'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0'%3E%3Crect width='60' height='60' fill='white' transform='translate(60 60) rotate(-180)'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E%0A");
  }
`;

const Role = styled.div`
  margin-top: 6px;
  font-weight: 500;
  line-height: 21px;
  color: ${({theme}) => `
    ${transparentize(.2, theme.colors.white)};
  `};
`;


/**
 * The returns course author component
 * @return {React.ReactElement}
 * @constructor
 */
export default function Author()
  : React.ReactElement {
  const context = React.useContext(CoursePromoContext);

  const {
    name,
    message,
    preview,
    video,
    role,
  } = context.author;

  /**
   * render avatar box
   * @return {JSX.Element}
   * @constructor
   */
  function renderAvatarBox() {
    if (preview) {
      return (
        <AvatarBox>
          <Avatar src={preview} alt={name}/>
          {renderVideoButton()}
        </AvatarBox>
      );
    }
  }

  /**
   * render video button
   * @return {JSX.Element}
   * @constructor
   */
  function renderVideoButton() {
    if (video) {
      return (
        <ButtonVideoWrapper>
          <ButtonVideo />
        </ButtonVideoWrapper>
      );
    }
  }

  /**
   * render content
   * @return {JSX.Element}
   * @constructor
   */
  function renderContent() {
    if (name || message) {
      return (
        <Content>
          {message && <Message>{message}</Message>}
          {name && <Name>{name}</Name>}
          {role && <Role>{role}</Role>}
        </Content>
      );
    }
  }

  return (
    <Container>
      {renderAvatarBox()}
      {renderContent()}
    </Container>
  );
}
